/* ============================================================
   mazo-marquee.js — the auto-scrolling, grabbable image ticker (sections/mazo-marquee.liquid).

   WHY THIS IS JS AND NOT A CSS @keyframes MARQUEE:
   a CSS animation can scroll, but it cannot be grabbed. The brief is auto-scroll + slow down on
   hover + grab and drag + release with inertia, and those need one shared source of truth for the
   offset. So a single rAF loop owns `x`, and every behaviour is expressed as a change to either the
   target speed or a one-off velocity added to it.

   THE MODEL (all in px, x is negative-going because it scrolls right to left):
     x         current offset of the track
     speed     px/sec of the automatic drift, eased toward `target` so nothing snaps
     target    speed we want right now: full / hover-slowed / 0 while held
     fling     extra velocity from a throw, decaying exponentially -> the inertia

   THE SEAMLESS LOOP: the section renders the images TWICE. One set is `setW` wide, so any x can be
   wrapped into (-setW, 0] and the result is visually identical. Wrapping with a modulo (not an
   if-subtract) means it stays correct no matter how far a single frame or a hard fling moves it.

   PHONES: THE DRIFT ENDS AT THE FIRST TOUCH (owner, 2026-10-22: "if a user interacts with it, stop the animation
   forever.. auto play only until user touches it"). On a phone-width screen (<=749px, the theme's phone line) the
   first press on the strip, a tap or a swipe, sets `stopped`; from then on the target speed is 0 for the rest of the
   page visit, so the strip only moves when a finger moves it (a swipe still coasts to a stop on its own momentum).
   Tablet and desktop keep the drift: there the hover slow-down already hands control to the visitor.
   It lasts for the page visit, not across visits: nothing is stored, so a returning visitor sees it move again.

   Reduced motion: no drift at all, but still draggable — the user stays in control, nothing moves
   on its own. Also pauses when off-screen (IntersectionObserver) and when the tab is hidden, so it
   is not burning frames invisibly.
   ============================================================ */
(function () {
  'use strict';

  var EASE = 6;          // how fast `speed` chases `target` (per second); higher = snappier
  var FLING_DECAY = 2.4; // inertia falloff per second; lower = the throw coasts longer
  var DRAG_SLOP = 4;     // px of movement before a press becomes a drag (so taps still click)
  var PHONE_MQ = '(max-width: 749px)';   // the theme's phone line (mazo-motion.js, mazo-footer.js, the token steps)

  function init(viewport) {
    var track = viewport.querySelector('[data-mazo-marquee-track]');
    if (!track) return;

    var baseSpeed = parseFloat(viewport.getAttribute('data-speed'));
    if (!isFinite(baseSpeed)) baseSpeed = 55;
    var hoverFactor = parseFloat(viewport.getAttribute('data-hover-factor'));
    if (!isFinite(hoverFactor)) hoverFactor = 0.25;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var x = 0, speed = reduce ? 0 : baseSpeed, target = reduce ? 0 : baseSpeed, fling = 0;
    var setW = 0;
    var hovering = false, dragging = false, pressed = false, visible = true;
    var stopped = false;   // phones only: set by the first touch, ends the auto drift for this page visit
    var pointerId = null, startPointerX = 0, startX = 0;
    var lastMoveX = 0, lastMoveT = 0, dragVel = 0;
    var raf = null, lastT = 0;

    /* THE WRAP DISTANCE is one set's width PLUS the track gap that follows it — not
       `scrollWidth / 2`. Halving only works if the two sets are exactly equal AND the gap between
       them is accounted for; measuring the first set directly and adding the real computed gap is
       correct regardless. Getting this wrong by even the gap width shows up as a small but very
       visible stutter once per loop. */
    function measure() {
      var set = track.querySelector('[data-mazo-marquee-set]');
      if (!set) { setW = 0; return; }
      var gap = parseFloat(getComputedStyle(track).columnGap);
      if (!isFinite(gap)) gap = 0;
      /* Measured from the FIRST set only. The second copy is deliberately PARTIAL (see the section
         Liquid), which does not affect this number at all — the wrap distance is one full set. */
      setW = set.getBoundingClientRect().width + gap;
      if (!setW) return;
      topUpDuplicate(set);
      x = wrap(x);
      draw();
    }

    /* THE PARTIAL COPY'S SAFETY NET.
       Only one viewport can be on screen at the moment the offset wraps, so the copy after the first
       set only has to be that wide. The Liquid renders six images, which is already wider than any
       phone — but a very wide or very short window can make the columns narrow enough that six do not
       reach across. Rather than guess a count that covers every case (and pay for it on every phone),
       the copy is topped up here from the real geometry: clone the next item in sequence until the copy
       exceeds the viewport. `dup.children.length % set.children.length` continues the original order,
       because the copy starts as items 0..5.
       On a phone this loop runs zero times. */
    function topUpDuplicate(set) {
      var dup = track.querySelector('[data-mazo-marquee-dup]');
      if (!dup || !set.children.length) return;
      var need = viewport.getBoundingClientRect().width;
      var guard = 0;
      while (dup.getBoundingClientRect().width < need && guard < 60) {
        var src = set.children[dup.children.length % set.children.length];
        if (!src) break;
        var clone = src.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        dup.appendChild(clone);
        guard++;
      }
    }

    function wrap(v) {
      if (!setW) return v;
      // JS % keeps the sign of the dividend, so normalise into (-setW, 0]
      var m = v % setW;
      return m > 0 ? m - setW : m;
    }

    function draw() {
      track.style.transform = 'translate3d(' + x.toFixed(2) + 'px,0,0)';
    }

    function frame(t) {
      raf = null;
      var dt = lastT ? Math.min((t - lastT) / 1000, 0.05) : 0; // clamp: a backgrounded tab can hand
      lastT = t;                                               // back a huge dt and jump the track

      if (!dragging) {
        // ease the drift toward its target
        speed += (target - speed) * Math.min(1, EASE * dt);
        // inertia from the last throw, decaying
        fling *= Math.exp(-FLING_DECAY * dt);
        if (Math.abs(fling) < 0.5) fling = 0;
        x = wrap(x - speed * dt + fling * dt);
        draw();
      }

      if (running()) schedule();
      else lastT = 0;
    }

    // Keep the loop alive only while there is something to do.
    function running() {
      if (!visible || document.visibilityState === 'hidden') return false;
      if (dragging) return false;                    // pointermove drives the transform directly
      return Math.abs(speed) > 0.05 || Math.abs(fling) > 0.05 || Math.abs(target) > 0.05;
    }
    function schedule() { if (raf === null) raf = requestAnimationFrame(frame); }
    function kick() { lastT = 0; schedule(); }

    function setTarget() {
      if (reduce || stopped) { target = 0; return; }
      target = hovering ? baseSpeed * hoverFactor : baseSpeed;
      kick();
    }

    /* ---- hover: slow down (pointer only; a touch "hover" would fight the drag) ---- */
    if (window.matchMedia && window.matchMedia('(hover:hover)').matches) {
      viewport.addEventListener('mouseenter', function () { hovering = true; setTarget(); });
      viewport.addEventListener('mouseleave', function () { hovering = false; setTarget(); });
    }

    /* ---- grab and drag ----
       Pointer Events so mouse, touch and pen share one path. `setPointerCapture` keeps the drag
       alive when the cursor leaves the element, which is what makes "grab, move, and let go
       somewhere else" behave. `touch-action:pan-y` in the CSS lets a vertical swipe still scroll
       the page while we take over the horizontal axis. */
    viewport.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      /* the first touch on a phone ends the drift for good. `speed` is zeroed too, not only the target, or the
         strip would keep sliding under the finger while it eased down. Checked here, at the touch, so a tablet
         turned to a phone width (or the reverse) is judged by the width it has right now. */
      if (!stopped && window.matchMedia && window.matchMedia(PHONE_MQ).matches) {
        stopped = true; speed = 0; setTarget();
      }
      pressed = true; dragging = false;
      pointerId = e.pointerId;
      startPointerX = lastMoveX = e.clientX;
      startX = x;
      lastMoveT = performance.now();
      dragVel = 0; fling = 0;
      try { viewport.setPointerCapture(pointerId); } catch (err) { /* not fatal */ }
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!pressed || e.pointerId !== pointerId) return;
      var dx = e.clientX - startPointerX;

      if (!dragging) {
        if (Math.abs(dx) < DRAG_SLOP) return;   // still could be a tap/click
        dragging = true;
        viewport.classList.add('is-grabbing');
      }

      x = wrap(startX + dx);
      draw();

      // running average of pointer velocity, for the throw
      var now = performance.now();
      var dt = (now - lastMoveT) / 1000;
      if (dt > 0) {
        var v = (e.clientX - lastMoveX) / dt;
        dragVel = dragVel * 0.7 + v * 0.3;      // smoothed, so one jittery frame cannot dominate
        lastMoveX = e.clientX; lastMoveT = now;
      }
      e.preventDefault();
    });

    function endDrag(e) {
      if (!pressed || (e && e.pointerId !== pointerId)) return;
      pressed = false;
      var wasDragging = dragging;
      dragging = false;
      viewport.classList.remove('is-grabbing');
      try { viewport.releasePointerCapture(pointerId); } catch (err) { /* already gone */ }
      pointerId = null;

      if (wasDragging) {
        /* Hand the pointer's velocity to `fling` so the track coasts to a stop instead of halting
           dead, then the normal drift takes back over. Capped so a violent swipe cannot launch it. */
        if (performance.now() - lastMoveT < 120) {
          fling = Math.max(-2600, Math.min(2600, dragVel));
        }
        /* A drag that ends over the ticker leaves the cursor there, so honour the hover state
           rather than jumping straight back to full speed. */
        setTarget();
      }
      kick();
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    /* A real drag must not also fire a click on the image link/figure underneath. */
    viewport.addEventListener('click', function (e) {
      if (viewport.classList.contains('is-grabbing')) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    // images are draggable by default in browsers; that would hijack the pointer
    viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* ---- lifecycle ---- */
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(track);
    else window.addEventListener('resize', measure);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        /* Release the compositor layer when the strip is off screen. `will-change:transform` keeps a
           promoted layer alive permanently, and on a phone this one is ~50 MB of texture — holding
           that while the band is nowhere near the viewport is pure memory pressure on iOS, which is
           exactly the kind of pressure that shows up as stutter elsewhere on the page. The transform
           itself still promotes the track while it is animating, so nothing is lost. */
        track.style.willChange = visible ? 'transform' : 'auto';
        if (visible) kick();
      }, { rootMargin: '120px' }).observe(viewport);
    }
    document.addEventListener('visibilitychange', function () { if (!document.hidden) kick(); });

    // images arrive late and change the track width, so re-measure as each one lands
    Array.prototype.forEach.call(track.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });

    measure();
    viewport.classList.add('is-ready');
    kick();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mazo-marquee]'), function (el) {
      if (!el.dataset.mazoMarqueeBound) { el.dataset.mazoMarqueeBound = '1'; init(el); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // the theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', boot);
})();
